import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Shared interaction treatment for the app shell. Touch-first controls keep a
 * 44px target while wide layouts with a fine primary pointer can stay compact.
 */
export const touchControlVariants = cva(
  'outline-none transition-[color,background-color,border-color,box-shadow,opacity,transform] ' +
    'focus-visible:ring-ring focus-visible:ring-[3px] focus-visible:ring-offset-1 ' +
    'focus-visible:ring-offset-background active:scale-[0.97] ' +
    'disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      density: {
        icon: 'min-h-11 min-w-11 md:pointer-fine:min-h-9 md:pointer-fine:min-w-9',
        answerIcon: 'size-11 md:pointer-fine:size-6',
        phoneIcon: 'min-h-11 min-w-11',
        text: 'min-h-11 md:pointer-fine:min-h-9',
      },
      surface: {
        ghost: 'hover:bg-sidebar-accent hover:text-foreground',
        quiet: 'hover:text-foreground',
      },
    },
    defaultVariants: {
      density: 'icon',
      surface: 'ghost',
    },
  },
);

type TouchIconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> &
  VariantProps<typeof touchControlVariants> & {
    label: string;
  };

export const TouchIconButton = forwardRef<HTMLButtonElement, TouchIconButtonProps>(
  ({ label, title, density, surface, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cn(touchControlVariants({ density, surface }), className)}
      {...props}
    />
  ),
);

TouchIconButton.displayName = 'TouchIconButton';
